import { useEffect, useMemo, useRef, useState } from 'react'

import { calcReportParamsSection, resolveReportParams } from '@aytm/app/stats/page/question-views/emd/lib/report-params'
import useParamsReseed, { isUnchanged } from '@aytm/app/stats/page/question-views/components/hooks/useParamsReseed'

// Owns the emotional-dial block state (view mode, line mode, persisted video position), seeded
// from the emd<key> report-param section and re-seeded whenever that section changes outside the
// block.
//
// The page view never reads the section itself: it receives an api on mount and pulls the
// payloads it needs (report-param section, export data) when a change is announced through
// onStateChange.
export default function useEmotionalDialState({ params, paramsRevision, onReady, onStateChange }) {
  const [state, setState] = useState(() => resolveReportParams({ section: params?.emd }))

  const snapshot = useRef()
  snapshot.current = state

  const api = useMemo(
    () => ({
      getReportParamsSection: () => calcReportParamsSection(snapshot.current),
      getExportData: () => ({ ...snapshot.current })
    }),
    []
  )

  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  useEffect(() => {
    onReadyRef.current?.(api)
  }, [api])

  // the page already holds what a re-seed reads, so it is announced as a seed, not written back
  const reseeding = useRef(false)
  useParamsReseed(params?.emd, paramsRevision, section => {
    const next = resolveReportParams({ section: section ?? undefined })
    if (isUnchanged(snapshot.current, next)) return
    reseeding.current = true
    setState(next)
  })

  // The reason tells the view what the change means: 'seed' is the resolved starting state,
  // 'state' a change the researcher made.
  const onStateChangeRef = useRef(onStateChange)
  onStateChangeRef.current = onStateChange

  const lastState = useRef(null)
  useEffect(() => {
    const reason = null === lastState.current || reseeding.current ? 'seed' : 'state'
    lastState.current = state
    reseeding.current = false

    onStateChangeRef.current?.(reason)
  }, [state])

  const onModelChange = useMemo(
    () =>
      (change = {}) =>
        setState(current =>
          Object.entries(change).every(([key, value]) => current[key] === value) ? current : { ...current, ...change }
        ),
    []
  )

  return { ...state, onModelChange }
}
